import { read, utils, write } from 'xlsx';
import { DEFAULT_SHEET_NAME } from '../../app/CanvasWidgets/components/TableWidget/const';

/**
 * function that read data from excel file
 * @param {File} file - excel file
 * @param {object} options - object with settings(read more about this in 'xlsx' documentation)
 * @returns {object} WorkBook - INFO
 */
export const getWorkBookFromExcel = (file, options) => read(file, options);

/**
 * generate string with date
 * @param {object} date - date object
 * @returns {string} - string that contains with data info
 */
export const generateDateString = date => {
  return `${date.getDate()}/${date.getMonth() +
    1}/${date.getFullYear()} @ ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
};

/**
 * convert excel sheet to datA
 * @param {object} sheet
 * @returns {Array} - array of arrays data
 */
const sheetToArrayOfArrays = sheet => {
  return utils.sheet_to_json(sheet, { header: 1, raw: false });
};

/**
 * function that filling array after parsing excel file and set default value ('')
 * @param {Array} array - array with parsed data
 * @returns {Array} - array with filled data with default value in every item
 */
const fillingArray = array => {
  for (let i = 0; i < array.length; i++) {
    const currentItem = array[i];
    if (currentItem === undefined || currentItem === null) {
      // eslint-disable-next-line no-param-reassign
      array[i] = '';
    }
  }
  return array;
};

/**
 * function that get data from workBook
 * @param {Array} arrOfSheets - array with sheets names
 * @param {object} workBook - workBook with excels data
 * @returns {*[][]} - array of arrays with filled data
 */
export const getDataFromExcelSheets = (arrOfSheets = [], workBook) => {
  return arrOfSheets.map(sheet => {
    const result = sheetToArrayOfArrays(workBook.Sheets[sheet]);

    const maxLength = [...result].sort((a, b) => b.length - a.length)[0].length;
    const filledResult = result.map(arr => {
      const arrLenght = arr.length;
      if (arrLenght < maxLength) {
        const emptyPart = new Array(maxLength - arrLenght).fill('');
        return fillingArray([...arr, ...emptyPart]);
      }
      return fillingArray(arr);
    });
    return filledResult.map(columnData => {
      return columnData.map(columnItem => ({ [DEFAULT_SHEET_NAME]: columnItem }));
    });
  });
};

/**
 * function that convert string from webBookData to octet
 * @param {string} string - string from workBook
 * @returns {ArrayBuffer} - octet
 */
export const convertStringToOctet = string => {
  const buffer = new ArrayBuffer(string.length);

  // convert string to arrayBuffer
  const view = new Uint8Array(buffer);

  // create uint8array as viewer
  for (let i = 0; i < string.length; i++) {
    // eslint-disable-next-line no-bitwise
    view[i] = string.charCodeAt(i) & 0xff;
  }

  // convert to octet
  return buffer;
};

/**
 * function that generate data from workBook
 * @param {object} workBook - workBook after converting from excel
 * @param {object} settings - object with settings (more details in 'xlsx' documentation)
 * @returns {any}
 */
export const generateDataFromWorkBook = (workBook, settings) => write(workBook, settings);

/**
 * function that merged sheets arrays to one with multiple data
 * @param {Array} array - sheets data
 * @returns {Array} array - array with final data
 */
// eslint-disable-next-line consistent-return
export const getFinalSheet = array => {
  for (let itemCount = 0; itemCount < array.length; itemCount++) {
    return array[itemCount].map((row, rowIndex) =>
      row.map((column, columnIndex) => ({
        ...column,
        ...array[0][rowIndex][columnIndex]
      }))
    );
  }
};

/**
 * function that convert array of arrays to workSheet
 * @param {Array} arrayOfArrays - data from tables row
 * @param {string} item - sheet name
 * @returns {object} - result
 */
export const convertArrayOfArraysToSheet = (arrayOfArrays, item) => {
  return utils.aoa_to_sheet(
    arrayOfArrays.map(dataItem => dataItem.rowData.map(rowDataItem => rowDataItem[item]))
  );
};

/**
 * functin that create and download file
 * @param {string} fileName - file name
 * @param {string} blob - blob with file data
 */
export const downloadFile = (fileName, blob) => {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.download = fileName;
  document.body.appendChild(a);
  a.href = URL.createObjectURL(blob);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
};

/**
 * function that exported table data to excel data and download file
 * @param {Array} sheetsList - array with sheets names
 * @param {Array} data - table data(array of arrays)
 */
export const exportExcelFile = (sheetsList, data) => {
  const currentDate = new Date();
  const webBook = utils.book_new();
  const datetime = generateDateString(currentDate);
  webBook.Props = {
    CreatedDate: currentDate
  };

  sheetsList.forEach(item => {
    webBook.SheetNames.push(item);
    webBook.Sheets[item] = convertArrayOfArraysToSheet(data, item);
  });

  const workBookData = generateDataFromWorkBook(webBook, {
    bookType: 'xlsx',
    type: 'binary'
  });
  const octet = convertStringToOctet(workBookData);
  const blob = new Blob([octet], { type: 'application/octet-stream' });

  downloadFile(`SizeFitTable${datetime}.xlsx`, blob);
};
